import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Tooltip,
  IconButton,
  Paper,
} from "@mui/material";

import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { MonthlyGroupPerformanceReport } from "./MonthlyGroupPerformanceReport";
import { MemberHealthScoreReport } from "./MemberHealthScoreReport";
import { GroupLiquidityReport } from "./GroupLiquidityReport";
import { SystemGrowthMetricsReport } from "./SystemGrowthMetricsReport";
import { AuditComplianceReport } from "./AuditComplianceReport";

import { fetchGroups as getGroups } from "../../services/groupService";
import { fetchAnyGroupMembers as getMembers } from "../../services/memberService";
import { useTranslation } from "react-i18next";

export const currencyFmt = (v) =>
  new Intl.NumberFormat("en-TZ", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(v || 0));

export const brand = "#800b33";
export const brandHover = "#6c0a2b";

export function ReportHeader({ title, subtitle, roleHint }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        {roleHint && (
          <Chip
            size="small"
            color="default"
            variant="outlined"
            label={roleHint}
            sx={{ alignSelf: "flex-start", fontSize: 11 }}
          />
        )}
      </Stack>
    </Box>
  );
}

export function FilterBar({
  filters,
  onFilterChange,
  onGenerate,
  onExport,
  showGroup = true,
  showMember = false,
  extraFilters = null,
  isGenerating = false,
}) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState(null);

  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(null);

  // Fetch all groups when the component mounts
  useEffect(() => {
    const fetchGroups = async () => {
      setGroupsLoading(true);
      setGroupsError(null);
      try {
        const data = await getGroups();
        setGroups(data || []);
      } catch (error) {
        console.error("Failed to fetch groups:", error);
        setGroupsError(t("Failed to load groups."));
      } finally {
        setGroupsLoading(false);
      }
    };
    if (showGroup) {
      fetchGroups();
    }
  }, [showGroup]);

  // Fetch members when a group is selected
  useEffect(() => {
    if (filters.group && showMember) {
      setMembers([]); // Reset members when group changes
      setMembersError(null);
      setMembersLoading(true);
      getMembers(filters.group)
        .then((data) => setMembers(data || []))
        .catch((err) => {
          console.error("Failed to fetch members:", err);
          setMembersError(t("Failed to load members."));
        })
        .finally(() => setMembersLoading(false));
    }
  }, [filters.group, showMember]);

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 3,
        boxShadow: 2,
        p: 2,
        bgcolor: "#fafafa",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} flex={1}>
          <TextField
            label={t("From")}
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.from || ""}
            onChange={(e) => onFilterChange("from", e.target.value)}
          />
          <TextField
            label={t("To")}
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.to || ""}
            onChange={(e) => onFilterChange("to", e.target.value)}
          />
          {showGroup && (
            <TextField
              label={t("Group")}
              size="small"
              fullWidth
              select
              value={filters.group || ""}
              onChange={(e) => onFilterChange("group", e.target.value)}
              disabled={groupsLoading}
              error={!!groupsError}
              helperText={groupsError}
            >
              {groupsLoading ? (
                <MenuItem disabled>{t("Loading groups...")}</MenuItem>
              ) : groupsError ? (
                <MenuItem disabled>{t("Error loading groups")}</MenuItem>
              ) : groups.length === 0 ? (
                <MenuItem disabled>{t("No groups found")}</MenuItem>
              ) : (
                groups.map((g) => (<MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
                ))
              )}
            </TextField>
          )}
          {showMember && (
            <TextField
              label={t("Member")}
              size="small"
              fullWidth
              select
              value={filters.member || ""}
              onChange={(e) => onFilterChange("member", e.target.value)}
              disabled={!filters.group || membersLoading || !!membersError}
              error={!!membersError}
              helperText={membersError}
            >
              {membersLoading ? (
                <MenuItem disabled>{t("Loading members...")}</MenuItem>
              ) : membersError ? (
                <MenuItem disabled>{t("Error loading members")}</MenuItem>
              ) : members.length === 0 ? (
                <MenuItem disabled>{t("Select a group first")}</MenuItem>
              ) : (
                members.map((m) => (<MenuItem key={m.user.id} value={m.id}>
                  {m.user.full_name}
                </MenuItem>
                ))
              )}
            </TextField>
          )}
          {extraFilters}
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            onClick={onGenerate}
            disabled={isGenerating}
            sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
            startIcon={<AssessmentOutlinedIcon />}
          >
            {isGenerating ? t("Generating...") : t("Generate")}
          </Button>
          <Tooltip title={t("Export as PDF")}>
            <IconButton size="small" onClick={() => onExport("pdf")}>
              <PictureAsPdfIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("Export as Excel")}>
            <IconButton size="small" onClick={() => onExport("excel")}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
}

export default {
  MonthlyGroupPerformanceReport,
  MemberHealthScoreReport,
  GroupLiquidityReport,
  SystemGrowthMetricsReport,
  AuditComplianceReport,
};
